import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { apiRequest, formatCurrency } from "../../../core/common/api";
import MetricCard from "../../../shared/MetricCard";
import { RANK_CLUBS, rankKey, clubLabel } from "../../../shared/Utils";

const currentMonth = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
  })
    .format(new Date())
    .slice(0, 7);

const previousMonth = () => {
  const [year, month] = currentMonth().split("-").map(Number);
  return new Date(Date.UTC(year, month - 2, 1)).toISOString().slice(0, 7);
};

const monthOptions = (count = 12) => {
  const [year, month] = currentMonth().split("-").map(Number);
  return Array.from({ length: count }, (_, index) =>
    new Date(Date.UTC(year, month - 1 - index, 1)).toISOString().slice(0, 7),
  );
};

const formatMonthLabel = (value: string) =>
  new Intl.DateTimeFormat("en-IN", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value}-01T00:00:00Z`));

const ProfitShare = () => {
  const [month, setMonth] = useState(previousMonth());
  const [companyProfit, setCompanyProfit] = useState("");
  const [preview, setPreview] = useState<any>(null);
  const [runs, setRuns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [checkingRanks, setCheckingRanks] = useState(false);

  const loadRuns = useCallback(async () => {
    try {
      const res = await apiRequest("/profit-share", "GET");
      setRuns(res.runs || []);
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to load profit share history", "error");
    }
  }, []);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        month,
        companyProfit: String(Number(companyProfit) || 0),
      });
      const res = await apiRequest(`/profit-share/preview?${params}`, "GET");
      setPreview(res);
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to preview", "error");
    } finally {
      setLoading(false);
    }
  };

  const checkRanks = async () => {
    setCheckingRanks(true);
    try {
      const res = await apiRequest("/profit-share/check-ranks", "POST", { month });
      await Swal.fire("Ranks updated", res.message, "success");
      setPreview(null);
    } catch (error: any) {
      Swal.fire("Rank check failed", error.message || "Unable to update ranks", "error");
    } finally {
      setCheckingRanks(false);
    }
  };

  const distribute = async () => {
    if (preview?.month !== month) {
      setPreview(null);
      await Swal.fire("Refresh preview", "Preview the selected month before crediting profit.", "info");
      return;
    }
    const confirm = await Swal.fire({
      title: `Credit ${month}?`,
      text: "This credits Profit Fund only. Members still request withdraw; you approve/reject manually. Cannot undo.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Credit profit fund",
      confirmButtonColor: "#1e3a6e",
    });
    if (!confirm.isConfirmed) return;

    setDistributing(true);
    try {
      const res = await apiRequest("/profit-share/distribute", "POST", {
        month,
        companyProfit: Number(companyProfit) || 0,
      });
      Swal.fire("Done", res.message || "Credited", "success");
      setPreview(null);
      void loadRuns();
      void loadPreview();
    } catch (error: any) {
      Swal.fire("Failed", error.message || "Unable to distribute", "error");
    } finally {
      setDistributing(false);
    }
  };

  const pools = preview?.pools || [];

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">MLM</div>
          <h1 className="page-title">
            Monthly <em>Profit Share</em>
          </h1>
          <div className="page-sub">
            Rank percentages and targets follow the saved club income settings.
            Each pool is shared equally among that month’s achievers and credited to Profit Fund.
          </div>
        </div>
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Distribute profit</h2>
          <p>Month is selectable for testing. Preview the chosen month, then credit Profit Fund.</p>
        </div>
        <div className="admin-card-body admin-form-grid">
          <div className="form-field">
            <label className="modal-label" htmlFor="profit-share-month">Month</label>
            <select
              id="profit-share-month"
              className="admin-input"
              value={month}
              disabled={loading || distributing || checkingRanks}
              onChange={(event) => {
                setMonth(event.target.value);
                setPreview(null);
              }}
            >
              {monthOptions().map((value) => (
                <option key={value} value={value}>
                  {formatMonthLabel(value)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label className="modal-label">Company profit (₹)</label>
            <input
              disabled={loading || distributing || checkingRanks}
              type="number"
              min="0"
              className="admin-input"
              value={companyProfit}
              onChange={(event) => { setCompanyProfit(event.target.value); setPreview(null); }}
              placeholder="0"
            />
          </div>
        </div>
        <div className="page-hdr-actions" style={{ padding: "0 20px 20px" }}>
          <button type="button" className="btn-sm btn-outline" disabled={checkingRanks || distributing || loading}
            onClick={() => void checkRanks()}>
            {checkingRanks ? "Checking ranks..." : "Check & Update Ranks"}
          </button>
          <button type="button" className="btn-sm btn-outline" disabled={checkingRanks || distributing || loading} onClick={() => void loadPreview()}>
            {loading ? "Loading..." : "Preview"}
          </button>
          <button
            type="button"
            className="btn-sm btn-primary"
            disabled={
              distributing || checkingRanks || loading ||
              !preview || preview.month !== month ||
              preview.alreadyDistributed ||
              !(Number(companyProfit) > 0)
            }
            onClick={() => void distribute()}
          >
            {preview?.alreadyDistributed
              ? "Already credited"
              : distributing
                ? "Crediting..."
                : "Credit Profit Fund"}
          </button>
        </div>
      </section>

      {preview && (
        <section className="metrics-grid">
          {pools.map((pool: any) => (
            <MetricCard
              key={pool.rank}
              metric={{
                label: pool.name || clubLabel(pool.rank),
                value: pool.achieverCount,
                icon: "🏆",
                tone: pool.achieverCount ? "green" : "muted",
                helper: `${pool.percent}% = ${formatCurrency(pool.poolAmount)} · ${formatCurrency(pool.perHead)} base; ${pool.extraPaiseCount || 0} members get ₹0.01 extra`,
              }}
            />
          ))}
        </section>
      )}

      {pools.map((pool: any) => (
        <section className="table-card" key={pool.rank}>
          <div className="table-head">
            <h2>
              {pool.name} ({pool.achieverCount})
            </h2>
            <p>
              {pool.percent}% of company profit
              {pool.achieverCount
                ? ` · ${formatCurrency(pool.perHead)} base per member; ${pool.extraPaiseCount || 0} receive ₹0.01 extra`
                : " · no achievers this month"}
            </p>
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Month sales</th>
                  <th>6-month sales</th>
                  <th>Leaders in team</th>
                  <th>Directors</th>
                  <th>Partners</th>
                  <th>Credit</th>
                </tr>
              </thead>
              <tbody>
                {pool.achievers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="empty-state">
                      No {pool.name} achievers.
                    </td>
                  </tr>
                ) : (
                  pool.achievers.map((row: any) => (
                    <tr key={row.memberId}>
                      <td>
                        <strong>{row.memberId}</strong>
                        <div className="table-muted">{row.name || "—"}</div>
                      </td>
                      <td>{row.monthSales}</td>
                      <td>{row.sixMonthSales}</td>
                      <td>{row.downline?.leader ?? 0}</td>
                      <td>{row.downline?.director ?? 0}</td>
                      <td>{row.downline?.partner ?? 0}</td>
                      <td>{formatCurrency(row.amount)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      <section className="table-card">
        <div className="table-head">
          <h2>Past credits</h2>
          <p>Each month can be credited only once</p>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Company profit</th>
                <th>Leader</th>
                <th>Director</th>
                <th>Partner</th>
                <th>Royalty</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-state">
                    No months credited yet.
                  </td>
                </tr>
              ) : (
                runs.map((run) => (
                  <tr key={run._id}>
                    <td>
                      <strong>{run.month}</strong>
                    </td>
                    <td>{formatCurrency(run.companyProfit)}</td>
                    {RANK_CLUBS.map((rank) => {
                      const pool = (run.pools || []).find((item: any) => rankKey(item.rank) === rankKey(rank));
                      return (
                        <td key={rank}>
                          {pool
                            ? `${pool.achieverCount} · ${formatCurrency(pool.perHead)} base${pool.extraPaiseCount ? `; ${pool.extraPaiseCount} × ₹0.01 extra` : ""}`
                            : "—"}
                        </td>
                      );
                    })}
                    <td>{run.distributedAt ? String(run.distributedAt).slice(0, 10) : "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <p className="text-muted-sm" style={{ padding: "12px 20px" }}>
          Profit income is added to eWallet. Withdrawal requests are managed in{" "}
          <Link to="/withdrawals">Withdrawals</Link> se approve / reject.
        </p>
      </section>
    </>
  );
};

export default ProfitShare;
